/**
 * High-level test helpers that operate on the active MockEnvironment singleton.
 *
 * These helpers eliminate common boilerplate in sandbox tests:
 *   - `waitFor(condition)` — poll until truthy (replaces flaky `await wait(ms)`)
 *   - `waitForFrames(n)` — wait for N message-queue flush cycles
 *   - `expectCanvas(canvas)` — fluent canvas assertion chain
 *   - `createScenario(opts)` — multi-player setup in one call
 *   - `assertNetworkConsistency(check)` — verify server/client state parity
 *
 * All helpers that need the environment auto-discover it via `getActiveEnv()`.
 * If `initializer()` hasn't been called (or the env was torn down), they throw
 * a clear error — never silently continue with stale state.
 *
 * Usage in test files:
 *   import { waitFor, waitForFrames, expectCanvas } from '@gotti/test-utils';
 */

import { getActiveEnv } from './initializer.js';
import { delay } from './wait.js';
import {
  getColorPresence,
  getPixelStats,
  assertCanvasNotBlank,
  assertColorPresent,
  readCanvasImageData,
} from './canvas-assertions.js';
import type { MockEnvironment, MockClient, MockAreaServer, CreateClientsOptions } from './types.js';

// ─── Types ───────────────────────────────────────────────────────────────────

export type BoundingBox = { x: number; y: number; width: number; height: number };

/**
 * Accepted sources for sprite / template matching.
 * - `ImageData` — works everywhere (Node tests + browser)
 * - `HTMLCanvasElement` — browser; pixel data extracted via getContext
 * - `HTMLImageElement` — browser; drawn to temp canvas, then extracted
 */
export type SpriteSource = ImageData | HTMLCanvasElement | HTMLImageElement;

// ─── wait / waitFor ──────────────────────────────────────────────────────────
//
// Both live in the leaf module `wait.ts` (the environment's game-clock waits use
// `waitFor` too, and must not import this file). This is their ONE public export
// path: `index.ts` re-exports this file and never `wait.ts`.
export { wait, waitFor } from './wait.js';
export type { WaitForOptions } from './wait.js';

function requireActiveEnv(helperName: string): MockEnvironment {
  try {
    return getActiveEnv();
  } catch {
    throw new Error(
      `[test-utils] ${helperName}() requires an active test environment. ` +
      `Call initializer() in your beforeEach/beforeAll before using this helper. ` +
      `If the environment was already torn down via teardownEnv(), you cannot ` +
      `call helpers after teardown.`
    );
  }
}

// ─── waitForFrames ───────────────────────────────────────────────────────────

/**
 * Wait for approximately `n` message-queue flush cycles.
 *
 * The mock environment uses `orderedSetTimeout` with real timers — messages
 * are flushed every ~33ms (30hz). This helper waits for `n` flush intervals
 * so queued messages have time to propagate.
 *
 * Does NOT require explicit env argument — validates the singleton is alive.
 *
 * ```ts
 * await waitForFrames(3);  // ~100ms — enough for most message round-trips
 * ```
 */
export async function waitForFrames(n = 1): Promise<void> {
  requireActiveEnv('waitForFrames');
  // Each flush cycle is ~33ms (1000/30). Wait for n cycles with small buffer.
  const msPerFrame = 34; // slightly above 33 to ensure the flush fires
  for (let i = 0; i < n; i++) {
    await delay(msPerFrame);
  }
}

// ─── Sprite matching (internal) ──────────────────────────────────────────────

interface SpriteMatch {
  x: number;
  y: number;
  /** 0–1 match quality (fraction of non-transparent pixels that matched). */
  score: number;
}

function spriteToImageData(sprite: SpriteSource): ImageData {
  if (sprite instanceof ImageData) return sprite;

  // HTMLCanvasElement
  if ('getContext' in sprite && typeof (sprite as any).getContext === 'function') {
    const canvas = sprite as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('[test-utils] Cannot get 2d context from sprite canvas');
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  // HTMLImageElement — draw to temporary canvas
  if (typeof HTMLImageElement !== 'undefined' && sprite instanceof HTMLImageElement) {
    const img = sprite;
    const c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width;
    c.height = img.naturalHeight || img.height;
    const ctx = c.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return ctx.getImageData(0, 0, c.width, c.height);
  }

  throw new Error(
    '[test-utils] Invalid sprite source — expected ImageData, HTMLCanvasElement, or HTMLImageElement'
  );
}

/**
 * Find all positions where `spriteData` matches within `canvasData`.
 *
 * Uses brute-force template matching with per-channel tolerance.
 * Transparent sprite pixels (alpha < 128) are ignored.
 * Results are deduplicated via non-maximum suppression.
 */
function findSpriteMatches(
  canvasData: ImageData,
  spriteData: ImageData,
  opts: { matchThreshold?: number; tolerance?: number } = {},
): SpriteMatch[] {
  const { matchThreshold = 0.9, tolerance = 10 } = opts;

  const cw = canvasData.width, ch = canvasData.height;
  const sw = spriteData.width, sh = spriteData.height;
  const cd = canvasData.data, sd = spriteData.data;

  // Build list of non-transparent sprite pixels
  const spritePixels: { dx: number; dy: number; r: number; g: number; b: number }[] = [];
  for (let sy = 0; sy < sh; sy++) {
    for (let sx = 0; sx < sw; sx++) {
      const si = (sy * sw + sx) * 4;
      if (sd[si + 3] >= 128) {
        spritePixels.push({ dx: sx, dy: sy, r: sd[si], g: sd[si + 1], b: sd[si + 2] });
      }
    }
  }

  if (spritePixels.length === 0) {
    throw new Error('[test-utils] Sprite has no non-transparent pixels');
  }

  const rawMatches: SpriteMatch[] = [];

  for (let cy = 0; cy <= ch - sh; cy++) {
    for (let cx = 0; cx <= cw - sw; cx++) {
      let matched = 0;
      for (const { dx, dy, r, g, b } of spritePixels) {
        const ci = ((cy + dy) * cw + (cx + dx)) * 4;
        const maxDiff = Math.max(
          Math.abs(cd[ci] - r),
          Math.abs(cd[ci + 1] - g),
          Math.abs(cd[ci + 2] - b),
        );
        if (maxDiff <= tolerance) matched++;
      }
      const score = matched / spritePixels.length;
      if (score >= matchThreshold) {
        rawMatches.push({ x: cx, y: cy, score });
      }
    }
  }

  // Non-maximum suppression — deduplicate overlapping matches
  const minDist = Math.max(sw, sh) / 2;
  const sorted = rawMatches.sort((a, b) => b.score - a.score);
  const kept: SpriteMatch[] = [];
  const used = new Set<number>();

  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    kept.push(sorted[i]);
    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(j)) continue;
      const dx = sorted[i].x - sorted[j].x;
      const dy = sorted[i].y - sorted[j].y;
      if (Math.sqrt(dx * dx + dy * dy) < minDist) {
        used.add(j);
      }
    }
  }

  return kept;
}

// ─── expectCanvas ────────────────────────────────────────────────────────────

export interface ExpectCanvasChain {
  /** Assert the canvas is not a single solid color. */
  toNotBeBlank(message?: string): ExpectCanvasChain;
  /** Assert a hex color occupies at least `minPercent`% of pixels. */
  toHaveColor(colorHex: string, opts?: { minPercent?: number; tolerance?: number }): ExpectCanvasChain;
  /** Assert a hex color occupies less than `maxPercent`% of pixels (default: 0.5%). */
  toNotHaveColor(colorHex: string, opts?: { maxPercent?: number; tolerance?: number }): ExpectCanvasChain;
  /** Assert the pixel at (x, y) matches `colorHex` within `tolerance`. */
  toHavePixelAt(x: number, y: number, colorHex: string, opts?: { tolerance?: number }): ExpectCanvasChain;
  /** Scope subsequent assertions to a sub-region of the canvas. */
  inRegion(rect: BoundingBox): ExpectCanvasChain;

  // ── Sprite assertions ────────────────────────────────────────────────────

  /**
   * Assert a sprite image appears at least `count` times on the canvas.
   * Transparent sprite pixels (alpha < 128) are ignored during matching.
   *
   * ```ts
   * expectCanvas(canvas).toHaveSprite(coinImageData, { count: 3 });
   * ```
   */
  toHaveSprite(sprite: SpriteSource, opts?: { count?: number; matchThreshold?: number; tolerance?: number }): ExpectCanvasChain;

  /**
   * Assert a sprite image appears within a specific bounding box.
   *
   * ```ts
   * expectCanvas(canvas).toHaveSpriteInBoundingBox(playerSprite, { x: 10, y: 20, width: 64, height: 64 });
   * ```
   */
  toHaveSpriteInBoundingBox(sprite: SpriteSource, rect: BoundingBox, opts?: { matchThreshold?: number; tolerance?: number }): ExpectCanvasChain;

  // ── Bounding-box color assertions ────────────────────────────────────────

  /**
   * Assert a hex color occupies at least `minPercent`% of pixels within a bounding box.
   * One-off check — does NOT change the chain's scoped region.
   */
  toHaveColorInBoundingBox(colorHex: string, rect: BoundingBox, opts?: { minPercent?: number; tolerance?: number }): ExpectCanvasChain;

  /**
   * Assert a hex color occupies less than `maxPercent`% of pixels within a bounding box.
   * One-off check — does NOT change the chain's scoped region.
   */
  toNotHaveColorInBoundingBox(colorHex: string, rect: BoundingBox, opts?: { maxPercent?: number; tolerance?: number }): ExpectCanvasChain;

  // ── Additional assertions ────────────────────────────────────────────────

  /**
   * Assert at least `count` distinct colors are present on the canvas.
   * Uses 6-bit-per-channel quantization. Stronger than `toNotBeBlank()`.
   *
   * ```ts
   * expectCanvas(canvas).toHaveMinColors(3); // at least 3 distinct colors rendered
   * ```
   */
  toHaveMinColors(count: number): ExpectCanvasChain;

  /**
   * Compare the canvas against a reference `ImageData`. Fails if fewer than
   * `threshold` (0–1, default 0.95) of pixels match within `tolerance`.
   *
   * ```ts
   * expectCanvas(canvas).toMatchSnapshot(savedReference, { threshold: 0.98 });
   * ```
   */
  toMatchSnapshot(reference: ImageData, opts?: { threshold?: number; tolerance?: number }): ExpectCanvasChain;
}

/**
 * Fluent canvas assertion builder. Chains multiple checks on a single canvas
 * without repeating `getColorPresence` / manual `expect()` calls.
 *
 * Takes the canvas, or a client — whose canvas is its `envOptions.canvas`. A 2d
 * canvas is read through its own context; a WebGL one through a 2d probe (see
 * `readCanvasImageData`), and one that cannot be read throws rather than read
 * as black.
 *
 * ```ts
 * expectCanvas(canvas)
 *   .toNotBeBlank()
 *   .toHaveColor('#ff0000', { minPercent: 5 })
 *   .toNotHaveColor('#00ff00');
 * expectCanvas(env.clients[id]).toHaveMinColors(2);
 * ```
 */
export function expectCanvas(source: HTMLCanvasElement | { envOptions?: { canvas?: HTMLCanvasElement } }): ExpectCanvasChain {
  if (typeof (source as HTMLCanvasElement).getContext === 'function') {
    return createExpectCanvasChain(source as HTMLCanvasElement, null);
  }
  const canvas = (source as { envOptions?: { canvas?: HTMLCanvasElement } }).envOptions?.canvas;
  if (!canvas) {
    const id = (source as { id?: unknown }).id;
    throw new Error(
      `[test-utils] expectCanvas: client ${id === undefined ? '(no id yet)' : JSON.stringify(id)} has no canvas ` +
      `(envOptions.canvas) — pass the canvas itself.`
    );
  }
  return createExpectCanvasChain(canvas, null);
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  if (h.length === 3) {
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ];
  }
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function getImageDataForRegion(
  canvas: HTMLCanvasElement,
  region: { x: number; y: number; width: number; height: number } | null,
): ImageData {
  return readCanvasImageData(canvas, region, '[test-utils] expectCanvas');
}

function createExpectCanvasChain(
  canvas: HTMLCanvasElement,
  region: { x: number; y: number; width: number; height: number } | null,
): ExpectCanvasChain {
  const getSource = () => getImageDataForRegion(canvas, region);

  const chain: ExpectCanvasChain = {
    toNotBeBlank(message?: string) {
      assertCanvasNotBlank(getSource(), message);
      return chain;
    },

    toHaveColor(colorHex: string, opts?: { minPercent?: number; tolerance?: number }) {
      const minPercent = opts?.minPercent ?? 0.5;
      const tolerance = opts?.tolerance ?? 10;
      const minFraction = minPercent / 100;
      assertColorPresent(getSource(), colorHex, minFraction, tolerance);
      return chain;
    },

    toNotHaveColor(colorHex: string, opts?: { maxPercent?: number; tolerance?: number }) {
      const maxPercent = opts?.maxPercent ?? 0.5;
      const tolerance = opts?.tolerance ?? 10;
      const presence = getColorPresence(getSource(), colorHex, tolerance);
      const maxFraction = maxPercent / 100;
      if (presence.fraction > maxFraction) {
        throw new Error(
          `[test-utils] Expected color ${colorHex} to be absent (< ${maxPercent}%), ` +
          `but found ${(presence.fraction * 100).toFixed(2)}% (${presence.matchedPixels} pixels)`
        );
      }
      return chain;
    },

    toHavePixelAt(x: number, y: number, colorHex: string, opts?: { tolerance?: number }) {
      const tolerance = opts?.tolerance ?? 10;
      // Adjust coordinates for region offset
      const px = region ? x + region.x : x;
      const py = region ? y + region.y : y;
      const pixel = getImageDataForRegion(canvas, { x: px, y: py, width: 1, height: 1 }).data;
      const [tr, tg, tb] = hexToRgb(colorHex);
      const dist = Math.max(
        Math.abs(pixel[0] - tr),
        Math.abs(pixel[1] - tg),
        Math.abs(pixel[2] - tb),
      );
      if (dist > tolerance) {
        throw new Error(
          `[test-utils] Expected pixel at (${x},${y}) to be ${colorHex} (tolerance=${tolerance}), ` +
          `but got rgb(${pixel[0]},${pixel[1]},${pixel[2]}) (distance=${dist})`
        );
      }
      return chain;
    },

    inRegion(rect: BoundingBox) {
      return createExpectCanvasChain(canvas, rect);
    },

    // ── Sprite assertions ──────────────────────────────────────────────────

    toHaveSprite(sprite: SpriteSource, opts?: { count?: number; matchThreshold?: number; tolerance?: number }) {
      const count = opts?.count ?? 1;
      const spriteData = spriteToImageData(sprite);
      const canvasImageData = getSource();
      const matches = findSpriteMatches(canvasImageData, spriteData, {
        matchThreshold: opts?.matchThreshold,
        tolerance: opts?.tolerance,
      });
      if (matches.length < count) {
        throw new Error(
          `[test-utils] Expected sprite to appear at least ${count} time(s), ` +
          `but found ${matches.length} match(es) (threshold=${opts?.matchThreshold ?? 0.9})`
        );
      }
      return chain;
    },

    toHaveSpriteInBoundingBox(sprite: SpriteSource, rect: BoundingBox, opts?: { matchThreshold?: number; tolerance?: number }) {
      const spriteData = spriteToImageData(sprite);
      const regionData = getImageDataForRegion(canvas, rect);
      const matches = findSpriteMatches(regionData, spriteData, {
        matchThreshold: opts?.matchThreshold,
        tolerance: opts?.tolerance,
      });
      if (matches.length === 0) {
        throw new Error(
          `[test-utils] Expected sprite in bounding box ` +
          `{x:${rect.x}, y:${rect.y}, w:${rect.width}, h:${rect.height}} ` +
          `but no matches found (threshold=${opts?.matchThreshold ?? 0.9})`
        );
      }
      return chain;
    },

    // ── Bounding-box color assertions ──────────────────────────────────────

    toHaveColorInBoundingBox(colorHex: string, rect: BoundingBox, opts?: { minPercent?: number; tolerance?: number }) {
      const minPercent = opts?.minPercent ?? 0.5;
      const tolerance = opts?.tolerance ?? 10;
      const regionImageData = getImageDataForRegion(canvas, rect);
      const minFraction = minPercent / 100;
      assertColorPresent(regionImageData, colorHex, minFraction, tolerance);
      return chain;
    },

    toNotHaveColorInBoundingBox(colorHex: string, rect: BoundingBox, opts?: { maxPercent?: number; tolerance?: number }) {
      const maxPercent = opts?.maxPercent ?? 0.5;
      const tolerance = opts?.tolerance ?? 10;
      const regionImageData = getImageDataForRegion(canvas, rect);
      const presence = getColorPresence(regionImageData, colorHex, tolerance);
      const maxFraction = maxPercent / 100;
      if (presence.fraction > maxFraction) {
        throw new Error(
          `[test-utils] Expected color ${colorHex} to be absent from bounding box ` +
          `{x:${rect.x}, y:${rect.y}, w:${rect.width}, h:${rect.height}} (< ${maxPercent}%), ` +
          `but found ${(presence.fraction * 100).toFixed(2)}% (${presence.matchedPixels} pixels)`
        );
      }
      return chain;
    },

    // ── Additional assertions ──────────────────────────────────────────────

    toHaveMinColors(count: number) {
      const stats = getPixelStats(getSource());
      if (stats.uniqueColors < count) {
        throw new Error(
          `[test-utils] Expected at least ${count} distinct colors, ` +
          `but found ${stats.uniqueColors}`
        );
      }
      return chain;
    },

    toMatchSnapshot(reference: ImageData, opts?: { threshold?: number; tolerance?: number }) {
      const threshold = opts?.threshold ?? 0.95;
      const tolerance = opts?.tolerance ?? 10;
      const actual = getSource();
      if (actual.width !== reference.width || actual.height !== reference.height) {
        throw new Error(
          `[test-utils] Snapshot size mismatch: canvas is ${actual.width}x${actual.height} ` +
          `but reference is ${reference.width}x${reference.height}`
        );
      }
      const totalPixels = actual.width * actual.height;
      let matched = 0;
      for (let i = 0; i < actual.data.length; i += 4) {
        const maxDiff = Math.max(
          Math.abs(actual.data[i] - reference.data[i]),
          Math.abs(actual.data[i + 1] - reference.data[i + 1]),
          Math.abs(actual.data[i + 2] - reference.data[i + 2]),
        );
        if (maxDiff <= tolerance) matched++;
      }
      const similarity = matched / totalPixels;
      if (similarity < threshold) {
        throw new Error(
          `[test-utils] Snapshot mismatch: ${(similarity * 100).toFixed(2)}% similarity ` +
          `(expected >= ${(threshold * 100).toFixed(2)}%). ` +
          `${totalPixels - matched} of ${totalPixels} pixels differ beyond tolerance=${tolerance}`
        );
      }
      return chain;
    },
  };

  return chain;
}

// ─── createScenario ──────────────────────────────────────────────────────────

export interface ScenarioOptions<TAuth = any> {
  /** Number of players to create. Default: 1. */
  playerCount?: number;
  /** If true, waits until all message queues flush after client creation. Default: true. */
  waitForSync?: boolean;
  /** Number of flush frames to wait for sync. Default: 5. */
  syncFrames?: number;
  /** Canvas dimensions. Default: 300x300. */
  canvasSize?: { width: number; height: number };
  /** Per-client auth callback. Default: `(i) => ({ id: \`player-\${i+1}\` })`. */
  authenticate?: (index: number) => TAuth;
  /** Game/area join config. */
  joinGame?: CreateClientsOptions['joinGame'];
  /** Per-client env options (canvas, container, etc.). */
  perClientEnvOptions?: CreateClientsOptions['perClientEnvOptions'];
  /** Callback after each client is created. */
  onClientCreated?: CreateClientsOptions['onClientCreated'];
}

export interface ScenarioResult {
  /** The mock environment. */
  env: MockEnvironment;
  /** Created mock clients in order. */
  clients: MockClient[];
  /** Canvases (if applicable, one per client). */
  canvases: HTMLCanvasElement[];
  /** Shortcut to env.areas. */
  areas: { [key: string]: MockAreaServer };
}

/**
 * Create a multi-player scenario in one call. Eliminates the 15+ lines of
 * canvas creation, createClients(), and wait-for-sync boilerplate.
 *
 * Uses the active environment singleton — `initializer()` must be called first.
 *
 * ```ts
 * const { clients, canvases } = await createScenario({ playerCount: 2 });
 * // Both players are connected and synced — start testing behavior
 * ```
 */
export async function createScenario<TAuth = any>(
  options?: ScenarioOptions<TAuth>,
): Promise<ScenarioResult> {
  const env = requireActiveEnv('createScenario');
  const playerCount = options?.playerCount ?? 1;
  const waitForSync = options?.waitForSync ?? true;
  const syncFrames = options?.syncFrames ?? 5;
  const canvasSize = options?.canvasSize ?? { width: 300, height: 300 };
  const authenticate = options?.authenticate ?? ((i: number) => ({ id: `player-${i + 1}` }) as any);

  const canvases: HTMLCanvasElement[] = [];

  // Create canvases if in a DOM environment
  const hasDOM = typeof document !== 'undefined';
  if (hasDOM) {
    for (let i = 0; i < playerCount; i++) {
      const canvas = document.createElement('canvas');
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      document.body.appendChild(canvas);
      canvases.push(canvas);
    }
  }

  const clientOptions: CreateClientsOptions = {
    authenticate,
    onClientCreated: options?.onClientCreated,
    joinGame: options?.joinGame,
  };

  if (hasDOM && !options?.perClientEnvOptions) {
    clientOptions.perClientEnvOptions = (i: number) => ({
      canvas: canvases[i],
      container: document.body,
    });
  } else if (options?.perClientEnvOptions) {
    clientOptions.perClientEnvOptions = options.perClientEnvOptions;
  }

  const clients = await env.createClients(playerCount, clientOptions);

  if (waitForSync) {
    await waitForFrames(syncFrames);
  }

  return {
    env,
    clients,
    canvases,
    areas: env.areas,
  };
}

// ─── assertNetworkConsistency ────────────────────────────────────────────────

/**
 * Assert that server and client state agree for every connected client.
 *
 * The `check` callback is called once per client with the area server,
 * client, and client ID. Throw inside `check` to fail the assertion.
 *
 * Uses the active environment singleton.
 *
 * ```ts
 * await assertNetworkConsistency((area, client, clientId) => {
 *   const serverPlayers = area.gameProcess.systemLookup[SYSTEMS.PM].players;
 *   const clientPlayers = client.process.systemLookup[SYSTEMS.SYNC].$api.players;
 *   if (clientPlayers.size !== serverPlayers.size) {
 *     throw new Error(`Client ${clientId} has ${clientPlayers.size} players, server has ${serverPlayers.size}`);
 *   }
 * });
 * ```
 */
export async function assertNetworkConsistency(
  check: (area: MockAreaServer, client: MockClient, clientId: string) => void | Promise<void>,
  options?: { areaId?: string },
): Promise<void> {
  const env = requireActiveEnv('assertNetworkConsistency');

  const areaIds = options?.areaId ? [options.areaId] : Object.keys(env.areas);
  const errors: string[] = [];

  for (const areaId of areaIds) {
    const area = env.areas[areaId];
    if (!area) {
      errors.push(`Area "${areaId}" not found in environment`);
      continue;
    }

    for (const [clientId, client] of Object.entries(env.clients)) {
      try {
        await check(area, client, clientId);
      } catch (err) {
        errors.push(
          `[area=${areaId}, client=${clientId}] ${err instanceof Error ? err.message : String(err)}`
        );
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(
      `[test-utils] Network consistency check failed:\n${errors.map(e => `  • ${e}`).join('\n')}`
    );
  }
}

// ─── recordFrames / expectFrames ─────────────────────────────────────────────

export interface RecordFramesOptions {
  /** Number of frames to capture. Default: 5. */
  count?: number;
  /** Interval between captures in ms. Default: 50. */
  intervalMs?: number;
}

export interface FrameCapture {
  /** Captured ImageData snapshots in chronological order. */
  frames: ImageData[];
}

/**
 * Capture a series of canvas snapshots over time. A WebGL canvas is read through
 * a 2d probe; these reads run between frames, so one created without
 * `preserveDrawingBuffer` reads back cleared once its frame was presented —
 * `env.expectRendering` reads inside the client's tick instead. A canvas that
 * cannot be read throws.
 *
 * ```ts
 * const capture = await recordFrames(canvas, { count: 10, intervalMs: 50 });
 * expectFrames(capture).toHaveChanged();
 * ```
 */
export async function recordFrames(
  canvas: HTMLCanvasElement,
  options?: RecordFramesOptions,
): Promise<FrameCapture> {
  const count = options?.count ?? 5;
  const intervalMs = options?.intervalMs ?? 50;

  const frames: ImageData[] = [];
  for (let i = 0; i < count; i++) {
    frames.push(readCanvasImageData(canvas, null, '[test-utils] recordFrames'));
    if (i < count - 1) {
      await delay(intervalMs);
    }
  }

  return { frames };
}

export interface ExpectFramesChain {
  /** Assert that at least two frames differ (something moved/changed). */
  toHaveChanged(message?: string): ExpectFramesChain;
  /** Assert the last N frames are identical (animation settled). Default N=3. */
  toStabilizeWithin(lastN?: number, message?: string): ExpectFramesChain;
  /** Scope frame comparison to a sub-region. */
  inRegion(rect: { x: number; y: number; width: number; height: number }): ExpectFramesChain;
}

/**
 * Fluent assertion chain for recorded frame captures.
 *
 * ```ts
 * expectFrames(capture).toHaveChanged();
 * expectFrames(capture).toStabilizeWithin(3);
 * ```
 */
export function expectFrames(capture: FrameCapture): ExpectFramesChain {
  return createExpectFramesChain(capture, null);
}

function extractRegion(
  frame: ImageData,
  region: { x: number; y: number; width: number; height: number } | null,
): Uint8ClampedArray {
  if (!region) return frame.data;
  const { x, y, width, height } = region;
  const result = new Uint8ClampedArray(width * height * 4);
  for (let row = 0; row < height; row++) {
    const srcOffset = ((y + row) * frame.width + x) * 4;
    const dstOffset = row * width * 4;
    result.set(frame.data.subarray(srcOffset, srcOffset + width * 4), dstOffset);
  }
  return result;
}

function framesEqual(a: Uint8ClampedArray, b: Uint8ClampedArray): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function createExpectFramesChain(
  capture: FrameCapture,
  region: { x: number; y: number; width: number; height: number } | null,
): ExpectFramesChain {
  const chain: ExpectFramesChain = {
    toHaveChanged(message?: string) {
      const { frames } = capture;
      if (frames.length < 2) {
        throw new Error('[test-utils] expectFrames.toHaveChanged: need at least 2 frames');
      }
      let anyDifferent = false;
      const first = extractRegion(frames[0], region);
      for (let i = 1; i < frames.length; i++) {
        const current = extractRegion(frames[i], region);
        if (!framesEqual(first, current)) {
          anyDifferent = true;
          break;
        }
      }
      if (!anyDifferent) {
        throw new Error(
          message ?? `[test-utils] Expected frames to change but all ${frames.length} frames were identical`
        );
      }
      return chain;
    },

    toStabilizeWithin(lastN = 3, message?: string) {
      const { frames } = capture;
      if (frames.length < lastN) {
        throw new Error(
          `[test-utils] expectFrames.toStabilizeWithin(${lastN}): only ${frames.length} frames captured`
        );
      }
      const tail = frames.slice(-lastN);
      const first = extractRegion(tail[0], region);
      for (let i = 1; i < tail.length; i++) {
        const current = extractRegion(tail[i], region);
        if (!framesEqual(first, current)) {
          throw new Error(
            message ??
              `[test-utils] Expected last ${lastN} frames to be identical (stabilized), ` +
              `but frame ${frames.length - lastN + i} differs from frame ${frames.length - lastN}`
          );
        }
      }
      return chain;
    },

    inRegion(rect: { x: number; y: number; width: number; height: number }) {
      return createExpectFramesChain(capture, rect);
    },
  };

  return chain;
}
